import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StatisticsPanel.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://tickets.fortunachile.cl:5000'
  : 'http://localhost:5000';

const StatisticsPanel = () => {
  const [stats, setStats] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    fetchData();
    
    // Intentar reconexión automática cada 30 segundos si hay error
    let retryInterval;
    if (error) {
      retryInterval = setInterval(() => {
        if (retryCount < 5) {
          setRetryCount(prev => prev + 1);
          fetchData();
        } else {
          clearInterval(retryInterval);
        }
      }, 30000);
    }

    return () => {
      if (retryInterval) clearInterval(retryInterval);
    };
  }, [error, retryCount]);

  const fetchData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Usar Promise.all para hacer ambas peticiones simultáneamente
      const [statsResponse, historyResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/stats`, { timeout: 10000 }),
        axios.get(`${API_BASE_URL}/api/history?limit=10`, { timeout: 10000 })
      ]);
      
      setStats(statsResponse.data || {});
      setHistory(historyResponse.data || []);
      setLastUpdated(new Date());
      setRetryCount(0);
    } catch (error) {
      console.error('Error obteniendo datos:', error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error) => {
    if (error.code === 'ECONNABORTED') {
      setError('Timeout: El servidor está tardando demasiado en responder');
    } else if (error.response) {
      // El servidor respondió con un código de error
      switch (error.response.status) {
        case 404:
          setError('Error: Endpoint no encontrado. Contacte al administrador.');
          break;
        case 500:
          setError('Error interno del servidor. Intente nuevamente.');
          break;
        case 503:
          setError('Servicio no disponible. El servidor puede estar en mantenimiento.');
          break;
        default:
          setError(`Error del servidor: ${error.response.status}`);
      }
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
      setError('No se pudo conectar con el servidor. Verifique su conexión.');
    } else {
      setError('Error inesperado: ' + error.message);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0 segundos';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    
    return parts.join(' ');
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '--:--:--';
      return new Date(dateString).toLocaleTimeString('es-CL', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return '--:--:--';
    }
  };

  const refreshData = () => {
    setRetryCount(0);
    fetchData();
  };

  if (loading) {
    return (
      <div className="statistics-panel">
        <h2>Estadísticas de Atención</h2>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-panel">
      <div className="stats-header">
        <h2>Estadísticas de Atención</h2>
        <button onClick={refreshData} className="refresh-btn" disabled={loading}>
          🔄 Actualizar
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <p>{error}</p>
            <p className="retry-info">
              Intentos: {retryCount}/5 • 
              {retryCount > 0 && ` Siguiente intento en ${30 - (retryCount * 5)} segundos`}
            </p>
            <button onClick={refreshData} className="retry-btn">
              Reintentar ahora
            </button>
          </div>
        </div>
      )}
      
      {lastUpdated && !error && (
        <div className="last-updated">
          Última actualización: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total de Tickets</h3>
          <div className="stat-value">{stats.total_tickets || 0}</div>
          <div className="stat-label">atendidos</div>
        </div>
        
        <div className="stat-card">
          <h3>Tiempo Promedio</h3>
          <div className="stat-value">
            {stats.avg_duration_seconds ? formatDuration(stats.avg_duration_seconds) : '0s'}
          </div>
          <div className="stat-label">por ticket</div>
        </div>
        
        <div className="stat-card">
          <h3>Tiempo Más Rápido</h3>
          <div className="stat-value">
            {stats.min_duration_seconds ? formatDuration(stats.min_duration_seconds) : '0s'}
          </div>
          <div className="stat-label">atención</div>
        </div>
        
        <div className="stat-card">
          <h3>Tiempo Más Lento</h3>
          <div className="stat-value">
            {stats.max_duration_seconds ? formatDuration(stats.max_duration_seconds) : '0s'}
          </div>
          <div className="stat-label">atención</div>
        </div>
      </div>

      <div className="history-section">
        <h3>Últimos Tickets Atendidos</h3>
        {history.length === 0 ? (
          <div className="no-data">
            <p>No hay datos de historial disponibles</p>
            {!error && <p>Los tickets aparecerán aquí después de ser atendidos</p>}
          </div>
        ) : (
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Hora Inicio</th>
                  <th>Hora Fin</th>
                  <th>Duración</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={index}>
                    <td className="ticket-number">{item.ticket_number || 'N/A'}</td>
                    <td>{formatDate(item.start_time)}</td>
                    <td>{formatDate(item.end_time)}</td>
                    <td className="duration">
                      {item.duration_seconds ? formatDuration(item.duration_seconds) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Información de debug (solo en desarrollo) */}
      {process.env.NODE_ENV === 'development' && error && (
        <div className="debug-info">
          <h4>Información de Debug:</h4>
          <p>Endpoint: {API_BASE_URL}/api/history</p>
          <p>Intentos: {retryCount}</p>
        </div>
      )}
    </div>
  );
};

export default StatisticsPanel;